import { share } from "@pocketsign/in-app-sdk";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { QRCodeCanvas } from "qrcode.react";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Copy from "~icons/material-symbols/content-copy-outline";
import QrCodeScanner from "~icons/material-symbols/qr-code-scanner";
import Share from "~icons/material-symbols/share-outline";
import Warning from "~icons/material-symbols/warning-outline";
import { css } from "../../../../styled-system/css";
import { Card } from "../../../components/card";
import Header from "../../../components/header";
import { INVITATION_END_DATE, TENANT_APP_NAMES } from "../../../config";
import { getCurrentTime } from "../../../libs/date";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { t } from "../../../libs/i18n";
import { getQRCodeURL } from "../../../libs/ps";
import { scanQrCode } from "../../../libs/qr-scanner";
import {
  getCityAddressWithCache,
  reportSDKResult,
  sdk,
} from "../../../libs/sdk";
import { getIsInTargetCity } from "../../../libs/tenant";

export const Route = createFileRoute("/_authed/invitation/")({
  loader: async ({ context: { tenant, client } }) => {
    const { invitationCode } = await client
      .getMyInvitation({})
      .catch((error) => {
        throw new AppError(
          ERROR_CODES.CONNECT_GET_MY_INVITATION_FAILED,
          error.message,
          {
            originalError: error,
          },
        );
      });
    return { tenant, invitationCode, client };
  },
  component: Invitation,
});

export function Invitation() {
  const navigate = useNavigate();
  const { tenant, invitationCode, client } = Route.useLoaderData();
  const [code, setCode] = useState("");
  const [applyLoading, setApplyLoading] = useState(false);

  const invitationUrl = useMemo(() => {
    return getQRCodeURL(tenant.serviceId, `/i/${invitationCode}`);
  }, [tenant.serviceId, invitationCode]);

  const onClickReader = useCallback(async () => {
    await scanQrCode({
      filterServiceIds: [tenant.serviceId],
      navigateFn: navigate,
    });
  }, [tenant.serviceId, navigate]);

  const shareText = t(
    "宮城県にお住まいの方限定！「みやポ」アプリ新規登録で「先着40万名に」3000円分のポイントをプレゼント！ 招待コードはこちら【{code}】 {url}",
    { code: invitationCode, url: invitationUrl },
  );

  const onClickShare = useCallback(() => {
    const result = share(sdk, {
      title: TENANT_APP_NAMES.miyagi,
      text: shareText,
    });
    void reportSDKResult("share", () => result);
  }, [shareText]);

  const onClickApply = useCallback(async () => {
    if (code.length === 0) {
      return;
    }

    const currentTime = await getCurrentTime();

    if (currentTime.date > INVITATION_END_DATE) {
      toast.error(t("キャンペーン期間外です"));
      return;
    }

    // 紹介される側（現在のユーザー）の住所が市内在住かチェック
    const cityAddress = await getCityAddressWithCache();
    if (!cityAddress || !getIsInTargetCity(tenant, cityAddress)) {
      toast.error(t("お住まいの地域では招待コードを利用できません"));
      return;
    }

    setApplyLoading(true);
    client
      .createMyInviter({ invitationCode: code })
      .then(() => {
        setCode("");
        toast.success(t("招待コードを適用しました"));
      })
      .catch(() => {
        toast.error(t("招待コードが利用できません"));
      })
      .finally(() => {
        setApplyLoading(false);
      });
  }, [code, client, tenant]);

  return (
    <>
      <Header
        title={t("友だち紹介")}
        onClickBack={() => {
          navigate({ to: "/" });
        }}
      />

      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          px: "16px",
          py: "24px",
        })}
      >
        <Card>
          <>
            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "12px",
              })}
            >
              <h1
                className={css({
                  color: "text.primary",
                  fontWeight: "600",
                  fontSize: "18px",
                })}
              >
                {t("紹介する")}
              </h1>

              <QRCodeCanvas value={invitationUrl} size={140} />

              <div
                className={css({
                  display: "flex",
                  gap: "8px",
                })}
              >
                <p
                  className={css({
                    fontSize: "32px",
                  })}
                >
                  {invitationCode}
                </p>

                <button
                  type="button"
                  className={css({
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "18px",
                  })}
                  onClick={() => {
                    navigator.clipboard.writeText(invitationCode);
                    toast.success(t("招待コードをコピーしました"));
                  }}
                >
                  <Copy />
                </button>
              </div>
            </div>

            <button
              type="button"
              className={css({
                mt: "16px",
                w: "100%",
                py: "8px",
                px: "16px",
                bg: "gray.100",
                rounded: "xl",
                border: "1px solid",
                borderColor: "border.secondary",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
              })}
              onClick={onClickShare}
            >
              <Share />
              <span>{t("シェア")}</span>
            </button>

            <div
              className={css({
                color: "text.secondary",
                bg: "surface.accentWarnLight",
                border: "1px solid",
                borderColor: "border.secondary",
                p: "16px",
                rounded: "md",
                mt: "16px",
                textAlign: "left",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              })}
            >
              <div
                className={css({
                  color: "text.primary",
                  display: "flex",
                  gap: "4px",
                })}
              >
                <Warning
                  className={css({
                    width: "24px",
                    height: "24px",
                    color: "surface.accentWarn",
                  })}
                />
                <div
                  className={css({
                    fontSize: "16px",
                  })}
                >
                  {t("紹介キャンペーンにカウントされるには")}
                </div>
              </div>
              <div
                className={css({
                  fontSize: "14px",
                })}
              >
                {t(
                  "ポケットサインアプリをダウンロードしていない場合、ダウンロード後に紹介コードの入力が必要です。",
                )}
              </div>
            </div>
          </>
        </Card>

        <Card>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
            })}
          >
            <h1
              className={css({
                color: "text.primary",
                fontWeight: "600",
                fontSize: "18px",
              })}
            >
              {t("紹介される")}
            </h1>

            <div
              className={css({
                display: "flex",
                gap: "8px",
                width: "100%",
              })}
            >
              <input
                type="text"
                inputMode="url"
                placeholder={t("招待コードを入力")}
                onChange={(e) => setCode(e.target.value)}
                value={code}
                className={css({
                  border: "1px solid",
                  borderColor: "border.secondary",
                  rounded: "md",
                  p: "8px",
                  px: "12px",
                  width: "100%",
                  WebkitAppearance: "none",
                  fontSize: "16px",
                })}
              />
              <button
                type="button"
                className={css({
                  bg: "surface.accentPrimary",
                  color: "white",
                  rounded: "md",
                  px: "18px",
                  flexShrink: 0,
                })}
                onClick={onClickApply}
                disabled={applyLoading}
              >
                {t("決定")}
              </button>
            </div>

            <button
              type="button"
              className={css({
                w: "100%",
                py: "8px",
                px: "16px",
                bg: "gray.100",
                rounded: "xl",
                border: "1px solid",
                borderColor: "border.secondary",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
              })}
              onClick={onClickReader}
            >
              <QrCodeScanner />
              <span>{t("読み取り")}</span>
            </button>
          </div>
        </Card>

        <Link
          to={"/invitation/detail"}
          className={css({
            color: "text.primary",
            textDecoration: "underline",
            textAlign: "center",
            pb: "32px",
          })}
        >
          {t("キャンペーン詳細")}
        </Link>
      </div>
    </>
  );
}
